import { api } from './api.ts'
import type { AmbitoRicerca, CartellaTrovata, FiltriThread, SegniThread, StatoThread, Colore, Per } from './posta.ts'

/**
 * L'elenco della posta come in Roundcube (541; decisione di Davide del 2/11/2026 sera): selezione multipla e
 * azioni in blocco, cartelle personali e «Sposta in…», vista per singoli messaggi, ordinamento e preferenze
 * personali salvate sul server. Le rotte stanno in apps/api/src/rotte/posta-elenco.ts.
 *
 * Cartelle e spostamenti esistono solo nel gestionale: i programmi di posta collegati alla casella con
 * l'IMAP (Dovecot di mx1cidis) non li vedono.
 */

// --- preferenze ------------------------------------------------------------------------------------

export type OrdineElenco = 'data' | 'mittente' | 'oggetto' | 'dimensione'
export type Verso = 'asc' | 'desc'

export type PreferenzePosta = {
  righe: 20 | 30 | 50 | 100
  ordine: OrdineElenco
  verso: Verso
  /** Per messaggi, predefinita dal 3/11/2026 (come Roundcube), o per conversazioni. */
  vista: 'conversazioni' | 'messaggi'
  /** Quando un messaggio aperto diventa letto: subito, dopo `secondi`, mai da solo. */
  lettura: 'apertura' | 'dopo' | 'mai'
  secondi: number
  /** Rispondendo, dove si scrive: sopra la citazione o sotto. */
  risposta: 'sopra' | 'sotto'
  /** Nella conversazione aperta il messaggio piu' recente sta in alto (predefinito dal 4/11/2026) o in fondo. */
  conversazione: 'recenti_sopra' | 'recenti_sotto'
  /** Dove si cerca: l'ultima scelta accanto al campo «Cerca» (3/11/2026). */
  ricerca: AmbitoRicerca
}

export const PREFERENZE_PREDEFINITE: PreferenzePosta = {
  righe: 30, ordine: 'data', verso: 'desc', vista: 'messaggi', lettura: 'apertura', secondi: 5, risposta: 'sopra',
  conversazione: 'recenti_sopra', ricerca: 'casella',
}

export const RIGHE = [20, 30, 50, 100] as const

export const leggiPreferenze = () => api.get<PreferenzePosta>('/posta/preferenze')
export const salvaPreferenze = (p: Partial<PreferenzePosta>) => api.put<PreferenzePosta>('/posta/preferenze', p)

/** Le scelte di ordine come le dice la pagina. */
export const ORDINI: { ordine: OrdineElenco; verso: Verso; nome: string }[] = [
  { ordine: 'data', verso: 'desc', nome: 'Data, dalla più recente' },
  { ordine: 'data', verso: 'asc', nome: 'Data, dalla meno recente' },
  { ordine: 'mittente', verso: 'asc', nome: 'Mittente, dalla A alla Z' },
  { ordine: 'mittente', verso: 'desc', nome: 'Mittente, dalla Z alla A' },
  { ordine: 'oggetto', verso: 'asc', nome: 'Oggetto, dalla A alla Z' },
  { ordine: 'oggetto', verso: 'desc', nome: 'Oggetto, dalla Z alla A' },
  { ordine: 'dimensione', verso: 'desc', nome: 'Dimensione, dalla più grande' },
  { ordine: 'dimensione', verso: 'asc', nome: 'Dimensione, dalla più piccola' },
]

export const nomeOrdine = (ordine: OrdineElenco, verso: Verso) =>
  ORDINI.find((o) => o.ordine === ordine && o.verso === verso)?.nome ?? ''

/** La risposta con la firma e la citazione: si scrive sopra la citazione (com'era) o sotto. */
export function testoRisposta(firma: string, citazione: string, dove: 'sopra' | 'sotto'): string {
  if (dove === 'sopra') return firma ? `\n\n${firma}${citazione}` : citazione
  // Sotto: la citazione in cima, poi lo spazio per scrivere e la firma.
  return `${citazione.replace(/^\n+/, '')}\n\n\n${firma ? `${firma}\n` : ''}`
}

// --- vista per messaggi ------------------------------------------------------------------------------

export type VoceMessaggio = {
  id: string
  thread_id: string
  casella_id: string
  cartella_id: string | null
  direzione: 'entrata' | 'uscita'
  da: string
  a: string[]
  oggetto: string
  anteprima: string
  data: string
  letto: boolean
  ha_allegati: boolean
  dimensione: number
  provenienza: 'falsa_cidis' | 'esterna_non_verificata' | 'esterna' | null
  /** Priorita' alta (539). */
  priorita_alta: boolean
  /** Non partito (3/11/2026): sta nelle «Bozze» con «Non inviato» e il motivo. */
  non_inviato?: { errore: string | null } | null
  /** Della conversazione. */
  stato: StatoThread
  persona_id: string | null
  /** Con la ricerca: la cartella del messaggio. */
  cartella?: CartellaTrovata | null
} & SegniThread

export type PaginaMessaggi = { messaggi: VoceMessaggio[]; prossimo_cursore: string | null; ordinate_recenti?: number | null }

function query(filtri: FiltriThread): string {
  const q = new URLSearchParams()
  if (filtri.cartella) q.set('cartella', filtri.cartella)
  if (filtri.stato) q.set('stato', filtri.stato)
  if (filtri.non_letti) q.set('non_letti', '1')
  if (filtri.bandierina) q.set('bandierina', filtri.bandierina)
  if (filtri.etichetta) q.set('etichetta', filtri.etichetta)
  if (filtri.ordine) q.set('ordine', filtri.ordine)
  if (filtri.verso) q.set('verso', filtri.verso)
  if (filtri.prima_di) q.set('prima_di', filtri.prima_di)
  q.set('limite', String(filtri.limite ?? 30))
  return q.toString()
}

/** I messaggi della casella, o della vista «Tutte le caselle» con `null`. Il testo cercato va nel corpo. */
export function elencaMessaggi(casellaId: string | null, filtri: FiltriThread) {
  const base = casellaId ? `/posta/caselle/${casellaId}/messaggi` : '/posta/tutte/messaggi'
  const cerca = filtri.cerca?.trim()
  if (!cerca) return api.get<PaginaMessaggi>(`${base}?${query(filtri)}`)
  return api.post<PaginaMessaggi>(`${base}/cerca`, {
    cartella: casellaId ? filtri.cartella ?? undefined : undefined, cerca, ambito: filtri.ambito, stato: filtri.stato,
    non_letti: filtri.non_letti ?? false, bandierina: filtri.bandierina ?? undefined,
    etichetta: filtri.etichetta ?? undefined, prima_di: filtri.prima_di ?? null, limite: filtri.limite ?? 30,
  })
}

/** Dimensione detta per esteso, per la colonna e per l'ordine per dimensione. */
export function dimensioneLeggibile(byte: number): string {
  if (byte < 1024) return `${byte} B`
  if (byte < 1024 * 1024) return `${Math.round(byte / 1024)} kB`
  return `${(byte / 1024 / 1024).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`
}

// --- azioni in blocco ----------------------------------------------------------------------------------

export type AzioneBlocco =
  | { tipo: 'stato'; stato: StatoThread }
  | { tipo: 'letto'; letto: boolean }
  | { tipo: 'sposta'; cartella_id: string }
  | { tipo: 'bandierina'; per: Per; colore: Colore | null }
  | { tipo: 'etichetta'; etichetta_id: string; messa: boolean }

export type EsitoBlocco = { eseguite: string[]; rifiutate: { id: string; codice: string }[] }

/** Conversazioni al massimo per richiesta (lo stesso limite dell'API): oltre si manda a pezzi. */
export const BLOCCO_MASSIMO = 200

export async function inBlocco(thread: string[], azione: AzioneBlocco): Promise<EsitoBlocco> {
  const esito: EsitoBlocco = { eseguite: [], rifiutate: [] }
  for (let i = 0; i < thread.length; i += BLOCCO_MASSIMO) {
    const parte = await api.post<EsitoBlocco>('/posta/thread/blocco', { thread: thread.slice(i, i + BLOCCO_MASSIMO), azione })
    esito.eseguite.push(...parte.eseguite)
    esito.rifiutate.push(...parte.rifiutate)
  }
  return esito
}

/** L'esito in una frase, se qualcosa non e' andato. */
export function testoRifiutate(esito: EsitoBlocco): string | null {
  if (!esito.rifiutate.length) return null
  const n = esito.rifiutate.length
  const motivi = new Set(esito.rifiutate.map((r) => r.codice))
  const perche = motivi.has('SOLA_LETTURA') ? ': su quelle caselle hai la sola lettura'
    : motivi.has('ACCESSO_PIENO_RICHIESTO') ? ': quelle per tutti le gestisce chi ha l’accesso pieno alla casella'
      : motivi.has('ETICHETTA_DI_ALTRA_CASELLA') ? ': l’etichetta per tutti appartiene a un’altra casella'
        : motivi.has('CARTELLA_DI_ALTRA_CASELLA') ? ': la cartella è di un’altra casella'
          : ': non ci sono più, o non le puoi vedere'
  return `${n === 1 ? 'Una conversazione non è stata cambiata' : `${n} conversazioni non sono state cambiate`}${perche}.`
}

export const spostaThread = (threadId: string, cartellaId: string) =>
  api.post<{ spostati: number }>(`/posta/thread/${threadId}/sposta`, { cartella_id: cartellaId })

// --- cartelle personali ----------------------------------------------------------------------------------

export const NOME_CARTELLA_MASSIMO = 60

export const creaCartella = (casellaId: string, nome: string, madreId: string | null) =>
  api.post<{ id: string; nome: string; madre_id: string | null }>(`/posta/caselle/${casellaId}/cartelle`,
    { nome, madre_id: madreId })

export const rinominaCartella = (id: string, nome: string) => api.patch<{ id: string; nome: string }>(`/posta/cartelle/${id}`, { nome })

export const contenutoCartella = (id: string) =>
  api.get<{ conversazioni: number; messaggi: number; figlie: number }>(`/posta/cartelle/${id}/contenuto`)

export const eliminaCartella = (id: string) =>
  api.elimina<{ eliminata: boolean; conversazioni: number; messaggi: number }>(`/posta/cartelle/${id}?conferma=1`)

export const segnaCartellaLetta = (id: string) => api.post<{ letti: number }>(`/posta/cartelle/${id}/lette`)
